import { IPaginationReturn } from './../../interfaces/IPaginationReturn';
import { useRequest } from '../providers/useRequest';
import AppError from '../../errors/AppError';
import IMedicalCertificate from '../../interfaces/IMedicalCertificate';
import IDiseaseClassification from '../../interfaces/IDiseaseClassification';
import { IAttachment } from '../../interfaces/IAttachment';
import IFormatForRecordingNumberOfHours from '../../interfaces/IFormatForRecordingNumberOfHours';

export interface IFilterMedicalCertificate {
  id?: number | null;
}
interface IFilterFollowup {
  status: string[];
  initialDate: string;
  finalDate: string;
  employeeId: String | null;
  statusIntegration: string;
  onlyAlerts?: boolean;
  localId?: string | null;
  protocolNumber?: string | null;
  allowDrafts?: boolean;
  typeDocumentId?: number | number[];
  costCenterId?: string | null;
  companyId?: string | null;
  subsidiaryId?: string | null;
}

interface IFilterFollowupReport {
  status: string[];
  dateDocumentInitial: string;
  dateDocumentFinal: string;
  dateShippingInitial: string;
  dateShippingFinal: string;
  companyId?: String | null;
  subsidiaryId?: String | null;
  localId?: String | null;
  postCode?: String | null;
  workShiftId?: String | null;
  workstationId?: String | null;
  typeDocumentId?: number | null;
  employeeId: String | null;
  statusIntegration: string;
  protocolNumber?: string | null;
  professionalName?: string | null;
  registerNumber?: string | null;
  disease?: string | null;
  timeQuantity?: number | null;
  gender?: string;
  costCenterId?: string | null;
}

interface IMonitoredNR1Response {
  isMonitoredNR1Porcentual: string;
}

interface IEmployeeRiskNR1 {
  incidenceEmployeePercentage: string;
}

interface IRiskByWorkstation {
  workstation: string;
  count: number;
  percentage: string;
}

interface IRisksByWorkstationResponse {
  risksByWorkstation: IRiskByWorkstation[];
}

interface IRiskBySector {
  locals: string;
  count: number;
  percentage: string;
}
interface IRiskBySectorResponse {
  risksBySector: IRiskBySector[];
}

interface IRisksGenders {
  riskByGenderMale: string;
  riskByGenderMalePercentual: string;
  riskByGenderFemale: string;
  riskByGenderFemalePercentual: string;
}

interface ITotalMedicalCertificatesWithNR1 {
  totalMedicalCertificatesWithNR1: string;
}

interface ICidWithHigherRisk {
  code: string;
  count: number;
  description: string;
}

interface IGetCidWithHigherRiskResponse {
  cidWithHigherRisk: ICidWithHigherRisk[];
}

interface IGetMedicalCertificateByDayAndMonthResponse {
  name: string;
  Documentos: number;
  Horas: number;
}

const useMedicalCertificateService = () => {
  const request = useRequest<IMedicalCertificate>();
  const diseaseRequest = useRequest<IDiseaseClassification>();
  const attachmentRequest = useRequest<IAttachment>();
  const formatForRecordingNumberOfHoursRequest = useRequest<IFormatForRecordingNumberOfHours>();
  const overlapRequest = useRequest<boolean>();
  const requestNR1 = useRequest<IMonitoredNR1Response>();
  const requestIncidenceEmployeeRiskNR1 = useRequest<IEmployeeRiskNR1>();
  const requestRiskByWorkstationNR1 = useRequest<IRisksByWorkstationResponse>();
  const requestRiskBySectorNR1 = useRequest<IRiskBySectorResponse>();
  const requestRiskGenders = useRequest<IRisksGenders>();
  const requestTotalMedicalCertificatesWithNR1 = useRequest<ITotalMedicalCertificatesWithNR1>();
  const requestCidWithHigherRisk = useRequest<IGetCidWithHigherRiskResponse>();
  const requestMedicalCertificateByDayAndMonth = useRequest<IGetMedicalCertificateByDayAndMonthResponse>();
  const requestAvailableYears = useRequest<{ years: number[] }>();

  const path = 'medical-certificate';

  const list = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
  ): Promise<IPaginationReturn<IMedicalCertificate[]>> => {
    return await request
      .getManyPaginated({
        path: `${path}?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listForFollowup = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
    filters: IFilterFollowup,
  ): Promise<IPaginationReturn<IMedicalCertificate[]>> => {
    return await request
      .postAndGetManyPaginated({
        path: `${path}/followup?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
        sendAuthorization: true,
        body: { ...filters },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listForFollowupReport = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
    filters: IFilterFollowupReport,
  ): Promise<IPaginationReturn<IMedicalCertificate[]>> => {
    return await request
      .postAndGetManyPaginated({
        path: `${path}/followup-report?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
        sendAuthorization: true,
        body: { ...filters },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listById = async (id: string): Promise<IMedicalCertificate> => {
    return await request
      .getOne({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listDiseasesByMedicalCertificateId = async (medicalCerticateId: string): Promise<IDiseaseClassification[]> => {
    return await diseaseRequest
      .getMany({
        path: `${path}/disease-classification/${medicalCerticateId}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listAttachmentsByMedicalCertificateId = async (medicalCerticateId: string): Promise<IAttachment[]> => {
    return await attachmentRequest
      .getMany({
        path: `${path}/attachments/${medicalCerticateId}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const createDiseasesClassification = async (resource: IDiseaseClassification): Promise<IDiseaseClassification> => {
    return await diseaseRequest
      .post({
        path: `${path}/disease-classification`,
        sendAuthorization: true,
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const updateDiseasesClassification = async (resource: IDiseaseClassification): Promise<IDiseaseClassification> => {
    return await diseaseRequest
      .post({
        path: `${path}/disease-classification`,
        sendAuthorization: true,
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const removeDiseasesClassification = async (id: string): Promise<void> => {
    await diseaseRequest
      .remove({
        path: `${path}/disease-classification/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const removeAttachment = async (id: string): Promise<void> => {
    await diseaseRequest
      .remove({
        path: `${path}/attachment/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const updateMedicalCertificateStatus = async (
    id: string,
    status?:
      | 'PENDING'
      | 'APPROVED'
      | 'APPROVEDBYMANAGER'
      | 'DISAPPROVED'
      | 'DISAPPROVEDBYMANAGER'
      | 'ARCHIVED'
      | 'ARCHIVEDBYMANAGER',
    eventDescription?: string,
  ): Promise<void> => {
    await diseaseRequest
      .patch({
        path: `${path}/status/${id}`,
        sendAuthorization: true,
        body: { status, eventDescription },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const create = async (medicalCertificate: IMedicalCertificate): Promise<IMedicalCertificate> => {
    return await request
      .post({
        path: `${path}`,
        sendAuthorization: true,
        body: { ...medicalCertificate },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const update = async (id: string, medicalCertificate: IMedicalCertificate): Promise<IMedicalCertificate> => {
    return await request
      .put({
        path: `${path}/${id}`,
        sendAuthorization: true,
        body: { ...medicalCertificate },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const sendAttachment = async (id: string, attachment: FormData): Promise<void> => {
    await request
      .patch({
        path: `${path}/attachment/${id}`,
        sendAuthorization: true,
        headers: {
          'content-type': 'multipart/form-data',
        },
        body: attachment,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getDownloadImage = async (
    medicalCertificateid: string,
    filename: string,
    token: string,
    description: string,
  ): Promise<Blob> => {
    return await request
      .post({
        path: `${path}/medical-certificate-download`,
        sendAuthorization: true,
        body: { medicalCertificateid, filename, token, description },
        responseType: 'blob',
      })
      .then(result => {
        return (result as unknown) as Blob;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const sendWhatsappMessage = async (medicalCertificateId: string, templateType: number): Promise<void> => {
    await request
      .put({
        path: `${path}/whatsapp-message/send`,
        sendAuthorization: true,
        body: { id: medicalCertificateId, templateType },
      })
      .then(() => {})
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const sendEmailMessage = async (
    medicalCertificateId: string,
    templateType: number,
    protocolNumber?: string,
    notificationEmails?: string,
    message?: string,
  ): Promise<void> => {
    await request
      .put({
        path: `${path}/email-message/send`,
        sendAuthorization: true,
        body: { id: medicalCertificateId, templateType, protocolNumber, notificationEmails, message },
      })
      .then(() => {})
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getFormatForRecordingNumberOfHours = async (): Promise<IFormatForRecordingNumberOfHours> => {
    return await formatForRecordingNumberOfHoursRequest
      .getOne({
        path: `${path}/format-for-recording-number-of-hours`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const isOverlap = async (employeeId: number, documentDate: string): Promise<boolean> => {
    return await overlapRequest
      .post({
        path: `${path}/is-overlaped`,
        sendAuthorization: true,
        body: { employeeId, documentDate },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const updateDeadline = async (id_MedicalCertificate: string, message: string, endTime: string): Promise<void> => {
    await overlapRequest
      .patch({
        path: `${path}/${id_MedicalCertificate}/deadline`,
        sendAuthorization: true,
        body: { message, endTime },
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getIsMonitoredNR1 = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<IMonitoredNR1Response> => {
    const parameter =
      competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestNR1
      .getOne({
        path: `${path}/is-monitored-nr1?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getIncidenceEmployeeRiskNR1 = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<IEmployeeRiskNR1> => {
    const parameter =
      competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestIncidenceEmployeeRiskNR1
      .getOne({
        path: `${path}/incidence-employee-risk-nr1?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getRiskNr1ByPosition = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<IRisksByWorkstationResponse> => {
    const parameter =
      competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestRiskByWorkstationNR1
      .getOne({
        path: `${path}/risk-nr1-position?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getRiskNr1BySector = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<IRiskBySectorResponse>  => {
    const parameter =
      competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestRiskBySectorNR1
      .getOne({
        path: `${path}/risk-nr1-sector?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  }

  const getRiskNr1Gender = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<IRisksGenders> => {
    const parameter =
      competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestRiskGenders
      .getOne({
        path: `${path}/risk-nr1-gender?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getTotalMedicalCertificatesWithNR1 = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<ITotalMedicalCertificatesWithNR1> => {
    const parameter =
      competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestTotalMedicalCertificatesWithNR1
      .getOne({
        path: `${path}/total-medical-certificate-nr1?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getCidWithHigherRisk = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<IGetCidWithHigherRiskResponse> => {
    const parameter =
      competenceDateFinal === undefined || competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestCidWithHigherRisk
      .getOne({
        path: `${path}/cid-with-higher-risk?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getMedicalCertificateByDayAndMonth = async (
    compentenceDate: string,
    competenceDateFinal?: string | null,
  ): Promise<any> => {
    const parameter =
      competenceDateFinal === undefined || competenceDateFinal === null
        ? `startDate=${compentenceDate}`
        : `startDate=${compentenceDate}&endDate=${competenceDateFinal}`;
    return await requestMedicalCertificateByDayAndMonth
      .getOne({
        path: `${path}/medical-certificate-day-month?${parameter}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const getAvailableYears = async (): Promise<number[]> => {
    return await requestAvailableYears
      .getOne({
        path: `${path}/available-years`,
        sendAuthorization: true,
      })
      .then(result => {
        return result?.years ?? [];
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  return {
    list,
    listDiseasesByMedicalCertificateId,
    listAttachmentsByMedicalCertificateId,
    listForFollowupReport,
    createDiseasesClassification,
    removeDiseasesClassification,
    removeAttachment,
    updateMedicalCertificateStatus,
    listById,
    listForFollowup,
    create,
    update,
    sendAttachment,
    sendWhatsappMessage,
    sendEmailMessage,
    updateDiseasesClassification,
    getFormatForRecordingNumberOfHours,
    isOverlap,
    updateDeadline,
    getDownloadImage,
    getIsMonitoredNR1,
    getIncidenceEmployeeRiskNR1,
    getRiskNr1ByPosition,
    getRiskNr1BySector,
    getRiskNr1Gender,
    getTotalMedicalCertificatesWithNR1,
    getCidWithHigherRisk,
    getMedicalCertificateByDayAndMonth,
    getAvailableYears,
  };
};

export { useMedicalCertificateService };
